/**
 * Wires app state to the analytics facade. Renders nothing.
 *
 * Mounted as a child of `<Providers>` in `src/app/app.tsx`, which is the
 * outermost point where auth and entitlements exist. The router is passed in
 * rather than read from context because this sits beside `<RouterProvider>`,
 * not inside it.
 */
import type { AnyRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAnalyticsIdentity } from "@/shared/analytics/use-analytics-identity";
import {
  resolveWorkspaceId,
  usePageView,
  useRouteSnapshot,
} from "@/shared/analytics/use-page-view";
import { useAuth } from "@/shared/auth/auth.context";
import { useIntegrationConsent } from "@/shared/integrations/integration-consent";
import { setAnalyticsConsent } from "@/shared/integrations/integration-runtime";

export function AnalyticsBridge({ router }: { router: AnyRouter }) {
  const { workspaces } = useAuth();
  const { analytics } = useIntegrationConsent();
  const snapshot = useRouteSnapshot(router);
  const workspaceId = resolveWorkspaceId(snapshot?.workspaceSlug ?? null, workspaces);

  // Register this effect before the identity and page-view effects below so a
  // persisted grant is active before either attempts its first SDK call.
  useEffect(() => {
    setAnalyticsConsent(analytics);
  }, [analytics]);

  usePageView(snapshot, workspaceId, analytics);
  useAnalyticsIdentity(workspaceId, analytics);

  return null;
}
